import { useCallback, useEffect, useState } from "react";
import { UploadSimple, CheckCircle, WarningCircle } from "@phosphor-icons/react";
import { BloomButton } from "@/components/styleguide/ui/BloomButton";
import { BloomBadge } from "@/components/styleguide/ui/BloomBadge";
import { api } from "../api";

export default function AdminImport() {
  const [batch, setBatch] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [batches, setBatches] = useState([]);

  const loadBatches = useCallback(() => api("/admin/import/batches").then(setBatches).catch(() => {}), []);
  useEffect(() => { loadBatches(); }, [loadBatches]);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true); setError(""); setBatch(null); setResult(null);
    try {
      const form = new FormData();
      form.append("file", file);
      setBatch(await api("/admin/import/validate", { method: "POST", form }));
      loadBatches();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    setBusy(true); setError("");
    try {
      setResult(await api(`/admin/import/${batch.batch_id}/confirm`, { method: "POST" }));
      setBatch(null);
      loadBatches();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-4xl" data-testid="admin-import">
      <h1 className="font-display text-3xl font-black tracking-tight text-foreground">Import Words</h1>
      <p className="mt-1 text-sm font-semibold text-muted-foreground">
        Upload a CSV with a <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">word</code> column.
        Optional columns: <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">word_type</code>, <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">year</code>, <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">difficulty</code>, <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">gs_11plus</code>, <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">meaning</code>, <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">synonyms</code>, <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">antonyms</code>, <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">sentences</code>, <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">memory_hint</code>, <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">tags</code>.
        Use <strong>|</strong> to separate multiple values (e.g. <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">bold|brave|fearless</code>). Words are validated then created as drafts.
      </p>

      <label className="mt-6 flex cursor-pointer flex-col items-center gap-2 rounded-3xl border-2 border-dashed border-border bg-card/60 p-10 text-center transition hover:border-primary/50" data-testid="csv-dropzone">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-accent"><UploadSimple size={26} weight="bold" className="text-primary-strong" /></span>
        <span className="font-display text-base font-black text-foreground">{busy ? "Working…" : "Choose a CSV file"}</span>
        <span className="text-xs font-bold text-muted-foreground">Max 2MB · UTF-8</span>
        <input type="file" accept=".csv" className="hidden" data-testid="csv-file-input" onChange={(e) => upload(e.target.files?.[0])} disabled={busy} />
      </label>

      {error && <p className="mt-3 flex items-center gap-1.5 text-sm font-bold text-destructive" data-testid="import-error"><WarningCircle size={16} weight="fill" /> {error}</p>}

      {result && (
        <div className="mt-4 flex items-center gap-2 rounded-2xl bg-success/15 px-4 py-3 text-sm font-extrabold text-success-ink" data-testid="import-result">
          <CheckCircle size={18} weight="fill" /> {result.created} words created as drafts — they're waiting in the Review Queue.
        </div>
      )}

      {batch && (
        <div className="mt-5 rounded-3xl border border-border bg-card p-5 shadow-soft" data-testid="validation-summary">
          <p className="font-display text-base font-black text-foreground">Validation summary — {batch.filename}</p>
          <div className="mt-3 grid grid-cols-2 gap-2.5 sm:grid-cols-5">
            {[
              ["Total rows", batch.counts.total, "neutral"],
              ["New", batch.counts.new, "success"],
              ["Already exist", batch.counts.existing, "blue"],
              ["Duplicates", batch.counts.duplicates, "warning"],
              ["Invalid", batch.counts.invalid, "primary"],
            ].map(([label, value, variant]) => (
              <div key={label} className="rounded-2xl bg-muted/60 p-3 text-center">
                <p className="font-display text-2xl font-black text-foreground">{value}</p>
                <BloomBadge variant={variant} className="px-2 py-0.5 text-[10px]">{label}</BloomBadge>
              </div>
            ))}
          </div>

          {batch.invalid_rows?.length > 0 && (
            <div className="mt-4">
              <p className="text-xs font-extrabold uppercase tracking-wide text-muted-foreground">Invalid rows</p>
              <div className="mt-1.5 space-y-1">
                {batch.invalid_rows.slice(0, 10).map((r, i) => (
                  <p key={i} className="text-sm font-semibold text-muted-foreground">Row {r.row}: “{r.value || "(empty)"}” — {r.reason}</p>
                ))}
              </div>
            </div>
          )}
          {batch.existing_words?.length > 0 && (
            <p className="mt-3 text-sm font-semibold text-muted-foreground"><b>Already in library:</b> {batch.existing_words.slice(0, 15).join(", ")}{batch.existing_words.length > 15 ? "…" : ""}</p>
          )}
          {batch.duplicate_words?.length > 0 && (
            <p className="mt-1 text-sm font-semibold text-muted-foreground"><b>Duplicates in file:</b> {batch.duplicate_words.slice(0, 15).join(", ")}{batch.duplicate_words.length > 15 ? "…" : ""}</p>
          )}

          <div className="mt-5 flex gap-2">
            <BloomButton size="sm" onClick={confirm} disabled={busy || batch.counts.new === 0} data-testid="import-continue-btn">
              Continue with {batch.counts.new} words
            </BloomButton>
            <BloomButton size="sm" variant="ghost" onClick={() => setBatch(null)} data-testid="import-cancel-btn">Cancel</BloomButton>
          </div>
        </div>
      )}

      <div className="mt-8">
        <p className="font-display text-base font-black text-foreground">Recent imports</p>
        <div className="mt-3 space-y-2">
          {batches.map((b) => (
            <div key={b.batch_id} className="flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-border bg-card px-4 py-3">
              <span className="min-w-0">
                <span className="block truncate font-display text-sm font-extrabold text-foreground">{b.filename}</span>
                <span className="block text-xs font-semibold text-muted-foreground">{new Date(b.uploaded_at).toLocaleString()}</span>
              </span>
              <span className="flex items-center gap-1.5 text-xs font-bold text-muted-foreground">
                {b.counts.new} new · {b.counts.invalid} invalid
                <BloomBadge variant={b.status === "completed" ? "success" : "warning"} className="px-2 py-0.5 text-[10px] capitalize">{b.status}</BloomBadge>
              </span>
            </div>
          ))}
          {batches.length === 0 && <p className="text-sm font-semibold text-muted-foreground">No imports yet.</p>}
        </div>
      </div>
    </div>
  );
}